// ── V135: 중국 매장 발주 — 수량 1 = 규격만큼 ─────────────────────────────────
// 감자: 규격 "10근", 단위 "근" → 발주 수량 1 = 10근, 2 = 20근.
// 규격 앞부분이 "숫자 + 단위"이고 그 단위가 품목의 단위와 같을 때만 묶음으로 본다.
//   "10근"/"10斤"/"10근/포대" + 단위 근 → 10근 묶음
//   "2근/봉" + 단위 봉 → 단위가 이미 봉(묶음)이라 그대로 (수량 1 = 1봉)
//   "1근", 규격 없음, "5-6근" → 그대로
//
// 단가가 1근 값인지 10근 한 묶음 값인지는 단가표의 기준량(g)으로 판단한다.
// 단가표의 단가는 원래 "기준량(g)만큼의 값"이다 (레시피 원가 = 사용량 ÷ 기준량 × 단가).
//   기준량이 5000g쯤(10근)이면 → 단가 = 한 묶음 값,  500g쯤(1근)이면 → 단가 = 1근 값
// 기준량이 없거나 무게로 못 바꾸는 단위(개 등)면 지금 화면 표시('단가/근')대로 1단위 값으로 본다.
import { unitToZh } from '../i18n/units';

const UNIT_KEY = {
  '근': 'jin', '斤': 'jin',
  'kg': 'kg', '킬로': 'kg', '킬로그램': 'kg', '公斤': 'kg', '千克': 'kg',
  'g': 'g', '그램': 'g', '克': 'g',
  'l': 'l', 'ℓ': 'l', '리터': 'l', '升': 'l',
  'ml': 'ml', '미리': 'ml', '밀리리터': 'ml', '毫升': 'ml',
  '개': 'ea', '个': 'ea',
};
// 1단위가 몇 g인지 (중국 1근 = 500g). 액체는 1ml ≈ 1g로 본다.
const GRAMS = { jin: 500, kg: 1000, g: 1, l: 1000, ml: 1 };

const unitKey = (u) => {
  const s = String(u == null ? '' : u).trim().toLowerCase();
  return UNIT_KEY[s] || s;
};

/** 규격에서 묶음 크기를 읽는다. 묶음이 아니면 1. */
export function specPack(spec, unit) {
  const s = String(spec == null ? '' : spec).trim();
  const m = s.match(/^(\d+(?:\.\d+)?)\s*([A-Za-zℓ가-힣一-鿿]+)/);
  if (!m) return 1;
  const n = Number(m[1]);
  if (!(n > 0) || n === 1) return 1;
  // 숫자·단위 바로 뒤에 또 숫자가 오면("10kg×2", "5근3") 묶음 크기를 확신할 수 없으니 그대로 둔다
  const rest = s.slice(m[0].length).trim();
  if (/^[×xX*]\s*\d/.test(rest) || /^\d/.test(rest)) return 1;
  const a = unitKey(m[2]);
  const u = String(unit == null ? '' : unit).trim() || '개';
  if (unitKey(u) === a) return n;
  const z = unitToZh(u);
  if (z && unitKey(z) === a) return n;
  return 1;
}

/** 단가가 한 묶음 값인지 (기준량으로 판단) */
export function priceIsPerPack(p, pack) {
  if (!p || !(pack > 1)) return false;
  const g = GRAMS[unitKey(String(p.unit || '').trim() || '개')];
  const net = Number(p.net_g);
  if (!g || !(net > 0)) return false;
  // 기준량이 '묶음 무게'와 '1단위 무게' 중 어디에 가까운가 (배율로 비교)
  return Math.abs(Math.log(net / (pack * g))) < Math.abs(Math.log(net / g));
}

/** { pack, perPack } — enabled(중국 매장)가 아니면 늘 묶음 1 */
export function orderPackInfo(p, enabled) {
  if (!enabled || !p) return { pack: 1, perPack: false };
  const pack = specPack(p.spec, p.unit);
  if (pack <= 1) return { pack: 1, perPack: false };
  return { pack, perPack: priceIsPerPack(p, pack) };
}

const round3 = (n) => Math.round(n * 1000) / 1000;

/** 발주 수량(묶음 수) → 실제 발주량(단위 기준). 감자 1 → 10 */
export const packQty = (qty, info) => round3((Number(qty) || 0) * (info?.pack || 1));

/** 줄 금액 */
export function lineAmount(qty, price, info) {
  const q = Number(qty) || 0;
  const pr = Number(price) || 0;
  if (!info || info.pack <= 1 || info.perPack) return q * pr;
  return q * info.pack * pr;
}

/** 1단위(근) 값 — 내역에 저장할 때 (수량×단가 = 금액이 되도록) */
export const unitPriceOf = (price, info) =>
  (info && info.pack > 1 && info.perPack ? (Number(price) || 0) / info.pack : (Number(price) || 0));

/** 레시피·재고에서 넘어온 필요량(기준량 묶음 수) → 단위 기준 양 */
export const needInUnits = (need, info) =>
  (info && info.pack > 1 && info.perPack ? round3(need * info.pack) : need);

/** 필요량(기준량 묶음 수) → 발주 수량(묶음 수, 올림) */
export function needToOrderQty(need, info) {
  const n = Number(need) || 0;
  if (!(n > 0)) return 0;
  const packs = info && info.pack > 1 && !info.perPack ? n / info.pack : n;
  return Math.ceil(packs - 1e-9);
}

/** 화면·문서용 숫자 (10, 1.5, 0.25) */
export const fmtQty = (n) => String(round3(Number(n) || 0));
